import type { ReactElement, ReactNode } from 'react';
import { Box, Text } from '@mantine/core';
import { ArrowDown, ArrowUp, Check, Minus, type LucideIcon } from 'lucide-react';
import type { MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import type { InteractionRecord } from '@/shared/analytics/AnalyticsTypes';
import { dimValue } from '@/shared/surfaces/InteractionRecordDrawer';
import { formatTokens, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import { recordFitScore, recordSuggestion, recordWaste, type FitSuggestionKind } from './ModelFitRecord';

/** Presentation for each routing verdict — colour, label, icon, and a sort rank (action-needed first). */
const SUGGESTION: Record<FitSuggestionKind, { color: string; label: string; icon: LucideIcon; rank: number }> = {
    stronger: { color: 'var(--bm-color-caution)', label: 'Use stronger', icon: ArrowUp, rank: 3 },
    lighter: { color: 'var(--bm-color-brand-accent)', label: 'Use lighter', icon: ArrowDown, rank: 2 },
    good: { color: 'var(--bm-color-success)', label: 'Good fit', icon: Check, rank: 1 },
    unknown: { color: 'var(--bm-color-text-dimmed)', label: '—', icon: Minus, rank: 0 },
};

/** A labeled routing-suggestion pill: use a stronger model, a lighter one, or keep it. */
function SuggestionPill({ record }: { record: InteractionRecord }): ReactElement {
    const { color, label, icon: Icon } = SUGGESTION[recordSuggestion(record)];
    return (
        <Box
            component="span"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                padding: '1px 8px',
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                color,
                background: `color-mix(in srgb, ${color} 12%, transparent)`,
                border: `1px solid color-mix(in srgb, ${color} 28%, transparent)`,
            }}
        >
            <Icon size={12} />
            {label}
        </Box>
    );
}

/**
 * The Model Fit prompt-grid columns: who ran each prompt, its department and
 * topic, the model it went to, the classified fit score, and the waste (cost +
 * tokens) when that model's tier didn't suit the task. Shared by the per-user
 * and per-model drill grids.
 */
export const MODEL_FIT_PROMPT_COLUMNS: MegaGridColumn<InteractionRecord>[] = [
    {
        key: 'person',
        header: 'Person',
        sortValue: (r) => humanUser(r.userId),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.userId)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => dimValue(r, 'user_department'),
        groupValue: (r) => dimValue(r, 'user_department'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'user_department') },
        render: (r) => (
            <Text size="xs" c="dimmed">
                {dimValue(r, 'user_department')}
            </Text>
        ),
    },
    {
        key: 'topic',
        header: 'Topic',
        sortValue: (r) => dimValue(r, 'task_category'),
        groupValue: (r) => dimValue(r, 'task_category'),
        filter: { type: 'enum', accessor: (r) => dimValue(r, 'task_category') },
        render: (r) => <Text size="xs">{dimValue(r, 'task_category')}</Text>,
    },
    {
        key: 'model',
        header: 'Model',
        sortValue: (r) => r.model,
        filter: { type: 'enum', accessor: (r) => r.model },
        render: (r) => (
            <Text size="xs" ff="monospace">
                {r.model}
            </Text>
        ),
    },
    {
        key: 'fit',
        header: 'Fit Score',
        align: 'right',
        sortValue: (r) => recordFitScore(r) ?? -1,
        render: (r): ReactElement => {
            const fit = recordFitScore(r);
            return (
                <Text size="xs" ff="monospace" fw={600}>
                    {fit === null ? '—' : fit}
                </Text>
            );
        },
    },
    {
        key: 'suggestion',
        header: 'Suggestion',
        sortValue: (r) => SUGGESTION[recordSuggestion(r)].rank,
        groupValue: (r) => SUGGESTION[recordSuggestion(r)].label,
        filter: { type: 'enum', accessor: (r) => SUGGESTION[recordSuggestion(r)].label },
        render: (r) => <SuggestionPill record={r} />,
        // In a group header, show the pill when the whole group shares one suggestion (i.e. grouped by suggestion).
        groupRender: (rows): ReactNode => {
            const kinds = new Set(rows.map((r) => recordSuggestion(r)));
            return kinds.size === 1 && rows[0] !== undefined ? <SuggestionPill record={rows[0]} /> : null;
        },
    },
    {
        key: 'wasteCost',
        header: 'Waste Cost',
        align: 'right',
        sortValue: (r) => recordWaste(r).cost,
        render: (r): ReactElement => {
            const { cost } = recordWaste(r);
            return (
                <Text size="xs" ff="monospace" c={cost > 0 ? undefined : 'dimmed'}>
                    {cost > 0 ? formatUsd(cost) : '—'}
                </Text>
            );
        },
    },
    {
        key: 'wasteTokens',
        header: 'Waste Tokens',
        align: 'right',
        sortValue: (r) => recordWaste(r).tokens,
        render: (r): ReactElement => {
            const { tokens } = recordWaste(r);
            return (
                <Text size="xs" ff="monospace" c={tokens > 0 ? undefined : 'dimmed'}>
                    {tokens > 0 ? formatTokens(tokens) : '—'}
                </Text>
            );
        },
    },
];
